import { ref, onMounted, onUnmounted } from 'vue';

export default function useWow(options = {}) {
  const scrollContainer = options.scrollContainer || window;
  const boxes = ref([]);
  const scrolled = ref(true);

  const init = () => {
    const element = document.documentElement;
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      start();
    } else {
      document.addEventListener('DOMContentLoaded', start);
    }
  };

  const start = () => {
    const animationNameCache = new Map();

    const scrollHandler = () => {
      scrolled.value = true;
    };

    const scrollCallback = () => {
      if (scrolled.value) {
        scrolled.value = false;
        boxes.value = boxes.value.filter((box) => {
          if (!box) {
            return true;
          }
          if (isVisible(box)) {
            show(box);
            return false;
          }
          return true;
        });
        if (!(boxes.value.length || options.live)) {
          stop();
        }
      }
    };

    const stop = () => {
      removeEvent(scrollContainer, 'scroll', scrollHandler);
      removeEvent(window, 'resize', scrollHandler);
    };

    const show = (box) => {
      applyStyle(box);
      box.className += ` ${options.animateClass}`;
      if (options.callback) {
        options.callback(box);
      }
      emitEvent(box, options.boxClass);
      addEvent(box, 'animationend', resetAnimation);
      addEvent(box, 'oanimationend', resetAnimation);
      addEvent(box, 'webkitAnimationEnd', resetAnimation);
      addEvent(box, 'MSAnimationEnd', resetAnimation);
    };

    const applyStyle = (box, hidden) => {
      const duration = box.getAttribute('data-wow-duration');
      const delay = box.getAttribute('data-wow-delay');
      const iteration = box.getAttribute('data-wow-iteration');
      animate(() => {
        customStyle(box, hidden, duration, delay, iteration);
      });
    };

    const isVisible = (box) => {
      const offset = box.getAttribute('data-wow-offset') || options.offset;
      const viewTop = scrollContainer === window ? window.pageYOffset : scrollContainer.scrollTop;
      const viewBottom =
        viewTop + Math.min(document.documentElement.clientHeight, innerHeight()) - offset;
      const top = offsetTop(box);
      const bottom = top + box.clientHeight;
      return top <= viewBottom && bottom >= viewTop;
    };

    const util = () => {
      return {
        extend(custom, defaults) {
          for (const key in defaults) {
            if (custom[key] == null) {
              custom[key] = defaults[key];
            }
          }
          return custom;
        },
        isMobile(agent) {
          return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(agent);
        },
        createEvent(event, bubble = false, cancel = false, detail = null) {
          const customEvent = document.createEvent('CustomEvent');
          customEvent.initCustomEvent(event, bubble, cancel, detail);
          return customEvent;
        },
        emitEvent(elem, event) {
          elem.dispatchEvent(new Event(event));
        },
        addEvent(elem, event, fn) {
          elem.addEventListener(event, fn, false);
        },
        removeEvent(elem, event, fn) {
          elem.removeEventListener(event, fn, false);
        },
        innerHeight() {
          return 'innerHeight' in window ? window.innerHeight : document.documentElement.clientHeight;
        },
      };
    };

    const offsetTop = (element) => {
      let top = element.offsetTop;
      while (element.offsetParent !== undefined) {
        element = element.offsetParent;
        top += element.offsetTop;
      }
      return top;
    };

    const customStyle = (box, hidden, duration, delay, iteration) => {
      if (hidden) {
        cacheAnimationName(box);
      }
      box.style.visibility = hidden ? 'hidden' : 'visible';
      if (duration) {
        vendorSet(box.style, { animationDuration: duration });
      }
      if (delay) {
        vendorSet(box.style, { animationDelay: delay });
      }
      if (iteration) {
        vendorSet(box.style, { animationIterationCount: iteration });
      }
      vendorSet(box.style, { animationName: hidden ? 'none' : cachedAnimationName(box) });
    };

    const animate = window.requestAnimationFrame || ((callback) => callback());

    const cacheAnimationName = (box) => {
      animationNameCache.set(box, animationName(box));
    };

    const cachedAnimationName = (box) => {
      return animationNameCache.get(box);
    };

    const vendorSet = (elem, properties) => {
      for (const name in properties) {
        const value = properties[name];
        elem[name] = value;
        for (const vendor of ['moz', 'webkit']) {
          elem[`${vendor}${name.charAt(0).toUpperCase()}${name.substr(1)}`] = value;
        }
      }
    };

    const vendorCSS = (elem, property) => {
      let result;
      const style = getComputedStyle(elem);
      result = style.getPropertyCSSValue(property);
      for (const vendor of ['moz', 'webkit']) {
        result = result || style.getPropertyCSSValue(`-${vendor}-${property}`);
      }
      return result;
    };

    const animationName = (box) => {
      let animationName;
      try {
        animationName = vendorCSS(box, 'animation-name').cssText;
      } catch (error) {
        animationName = getComputedStyle(box).getPropertyValue('animation-name');
      }
      return animationName === 'none' ? '' : animationName;
    };

    onMounted(init);
    onUnmounted(stop);

    return {
      init,
      start,
      stop,
      sync: (element) => {
        if (!MutationObserver.notSupported) {
          return doSync(element);
        }
      },
    };
  };

  return init;
}
